import React, { useState, useEffect } from 'react';
import {
    Card,
    CardContent,
    Typography,
    Button,
    Box,
    CircularProgress,
    Chip,
    Alert,
    Grid,
    Stack,
    Dialog,
    IconButton,
} from '@mui/material';
import {
    Quiz as QuizIcon,
    Timer,
    Assignment,
    CheckCircle,
} from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import { StaticQuizDTO } from '../../services/quizService';
import { getGuestTopicQuiz, submitGuestQuizAttempt } from '../../services/guestApi';
import { useGuestContext } from '../../context/GuestContext';
import QuizRunner from './QuizRunner';

interface GuestPracticeQuizCardProps {
    topicId: number;
}

const GuestPracticeQuizCard: React.FC<GuestPracticeQuizCardProps> = ({ topicId }) => {
    const { guestId } = useGuestContext();
    const [checkingQuiz, setCheckingQuiz] = useState(true);
    const [availableQuizzes, setAvailableQuizzes] = useState<StaticQuizDTO[]>([]);
    const [currentQuizIndex, setCurrentQuizIndex] = useState(0);
    const [activeQuiz, setActiveQuiz] = useState<StaticQuizDTO | null>(null);
    const [blockedReason, setBlockedReason] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        checkAvailableQuiz();
    }, [topicId, guestId]);

    const checkAvailableQuiz = async () => {
        if (!guestId) {
            setCheckingQuiz(false);
            setAvailableQuizzes([]);
            return;
        }
        setCheckingQuiz(true);
        try {
            const list = await getGuestTopicQuiz(guestId, topicId);
            const withTopicId = list.map((q) => ({ ...q, topicId: q.topicId ?? topicId }));
            setAvailableQuizzes(withTopicId);
        } catch (_) {
            setAvailableQuizzes([]);
        } finally {
            setCheckingQuiz(false);
        }
    };

    const handleStartPractice = async () => {
        if (availableQuizzes.length > 0) {
            setActiveQuiz(availableQuizzes[currentQuizIndex]);
            return;
        }
        if (!guestId) return;
        setLoading(true);
        setBlockedReason(null);
        try {
            const list = await getGuestTopicQuiz(guestId, topicId);
            const withTopicId = list.map((q) => ({ ...q, topicId: q.topicId ?? topicId }));
            setAvailableQuizzes(withTopicId);
            if (withTopicId.length > 0) {
                setActiveQuiz(withTopicId[0]);
            } else {
                setBlockedReason('No practice quiz available for this topic.');
            }
        } catch (_) {
            setBlockedReason('Failed to load quiz. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleQuizComplete = () => {};

    const handleCloseQuiz = () => setActiveQuiz(null);

    const submitAttempt = async (data: {
        quizId: number;
        topicId: number;
        answers: Record<string, unknown>;
        score: number;
        correctAnswers: number;
        timeTakenMinutes: number;
        passed: boolean;
    }) => {
        await submitGuestQuizAttempt({
            quizId: data.quizId,
            topicId: data.topicId,
            answers: data.answers,
            score: data.score,
            correctAnswers: data.correctAnswers,
            timeTakenMinutes: data.timeTakenMinutes,
            passed: data.passed,
        });
    };

    const quiz = availableQuizzes[currentQuizIndex] ?? activeQuiz;

    return (
        <>
            <Card
                elevation={2}
                sx={{
                    mb: 2,
                    borderLeft: '6px solid',
                    borderColor: availableQuizzes.length > 0 ? 'success.main' : 'warning.main',
                }}
            >
                <CardContent>
                    <Box display="flex" alignItems="center" gap={1.5} mb={1.5}>
                        <QuizIcon color={availableQuizzes.length > 0 ? 'success' : 'warning'} />
                        <Typography variant="h6" fontWeight={600}>
                            Practice Quiz
                        </Typography>
                        {checkingQuiz && <CircularProgress size={16} />}
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Try the quiz for this topic (guest: max 2 attempts total).
                    </Typography>
                    {blockedReason && (
                        <Alert severity="info" sx={{ mt: 1 }}>{blockedReason}</Alert>
                    )}
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleStartPractice}
                        disabled={loading || checkingQuiz}
                        startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <QuizIcon />}
                    >
                        {loading ? 'Loading...' : checkingQuiz ? 'Checking...' : availableQuizzes.length > 0 ? 'Start Quiz' : 'Try Quiz'}
                    </Button>
                </CardContent>
            </Card>

            <Dialog open={!!activeQuiz} fullScreen onClose={handleCloseQuiz}>
                <Box sx={{ position: 'relative', height: '100%', bgcolor: '#f5f5f5' }}>
                    <IconButton
                        onClick={handleCloseQuiz}
                        aria-label="close"
                        sx={{ position: 'absolute', right: 16, top: 16, zIndex: 1200 }}
                    >
                        <CloseIcon />
                    </IconButton>
                    {activeQuiz && (
                        <QuizRunner
                            quiz={activeQuiz}
                            mode="PRACTICE"
                            onComplete={handleQuizComplete}
                            onExit={handleCloseQuiz}
                            submitAttempt={submitAttempt}
                        />
                    )}
                </Box>
            </Dialog>
        </>
    );
};

export default GuestPracticeQuizCard;
